/**
 * src/components/ledger/AdminEntryModal.tsx
 * Admin modal for creating direct transactions and editing existing entries
 */

import React, { useState, useEffect } from 'react';
import { Plus, Edit2, CheckCircle2 } from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { GlassInput } from '../ui/GlassInput';
import { GlassButton } from '../ui/GlassButton';
import { ReceiptUploadField } from '../ui/ReceiptUploadField';
import { QuickExpensePicker } from './QuickExpensePicker';
import { saveRecentPreset, type ExpensePreset } from '../../utils/frequentExpenses';
import type { UserItem, CategoryItem, TransactionItem } from '../../services/api';

interface AdminEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<any>;
  users: UserItem[];
  categories: CategoryItem[];
  editTransaction?: TransactionItem | null;
}

export const AdminEntryModal: React.FC<AdminEntryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  users,
  categories,
  editTransaction
}) => {
  const isEditing = Boolean(editTransaction);

  // Exclude deactivated users when creating new entries
  const activeUsers = users.filter(u => u.is_active !== 0 || (editTransaction && u.id === editTransaction.user_id));

  const [userId, setUserId] = useState<string>('');
  const [transactionType, setTransactionType] = useState<'paid' | 'receive'>('paid');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [transactionDate, setTransactionDate] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Pre-fill fields when editing or opening
  useEffect(() => {
    if (editTransaction) {
      setUserId(String(editTransaction.user_id));
      setTransactionType(editTransaction.transaction_type);
      setAmount(String(editTransaction.amount));
      setDescription(editTransaction.description);
      setCategoryId(editTransaction.category_id ? String(editTransaction.category_id) : '');
      setTransactionDate(editTransaction.transaction_date ? editTransaction.transaction_date.slice(0, 16) : '');
      setDueDate(editTransaction.due_date ? editTransaction.due_date.slice(0, 10) : '');
      setReceiptUrl(editTransaction.receipt_url || null);
    } else {
      setUserId(activeUsers[0]?.id ? String(activeUsers[0].id) : '');
      setTransactionType('paid');
      setAmount('');
      setDescription('');
      setCategoryId(categories[0]?.id ? String(categories[0].id) : '');
      setReceiptUrl(null);
      
      // Default to current local time in YYYY-MM-DDTHH:mm format
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setTransactionDate(now.toISOString().slice(0, 16));
      setDueDate('');
    }
    setFormError('');
  }, [editTransaction, isOpen, users, categories]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!userId) {
      setFormError('Please select a user.');
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setFormError('Please enter a valid positive amount.');
      return;
    }

    if (!description.trim()) {
      setFormError('Please enter a description or item name.');
      return;
    }

    const selectedCategoryObj = categories.find(c => String(c.id) === categoryId);

    setIsSubmitting(true);
    try {
      await onSubmit({
        user_id: parseInt(userId, 10),
        transaction_type: transactionType,
        amount: parsedAmount,
        description: description.trim(),
        category_id: categoryId ? parseInt(categoryId, 10) : null,
        category_name: selectedCategoryObj?.name || null,
        transaction_date: transactionDate ? transactionDate.replace('T', ' ') + ':00' : undefined,
        due_date: dueDate || null,
        receipt_url: receiptUrl || null
      });

      // Save to recent presets for 1-tap quick selection next time
      saveRecentPreset({
        label: description.trim(),
        amount: parsedAmount,
        description: description.trim(),
        categoryName: selectedCategoryObj?.name || 'General',
        icon: transactionType === 'receive' ? '💰' : '☕'
      });

      onClose();
    } catch (err: any) {
      setFormError(err.message || 'Operation failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickPresetSelect = (preset: ExpensePreset) => {
    setAmount(String(preset.amount));
    setDescription(preset.description);
    if (preset.categoryName) {
      const matched = categories.find(c =>
        c.name.toLowerCase().includes(preset.categoryName.toLowerCase()) ||
        preset.categoryName.toLowerCase().includes(c.name.toLowerCase())
      );
      if (matched) {
        setCategoryId(String(matched.id));
      }
    }
  };

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Transaction & Adjust Balance' : 'Record Direct Transaction'}
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {formError && (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--crimson-tint)',
              border: '1px solid rgba(244, 63, 94, 0.3)',
              color: 'var(--crimson-light)',
              fontSize: '0.84rem'
            }}
          >
            {formError}
          </div>
        )}

        {/* User Dropdown */}
        <div>
          <label style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
            Customer / User
          </label>
          <select
            className="glass-select"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
          >
            {activeUsers.map(u => (
              <option key={u.id} value={u.id}>
                {u.username} {u.current_balance !== undefined ? `(Balance: ₹${u.current_balance.toFixed(2)})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Type Toggle: Paid by Admin (Debit -) / Received by Admin (Credit +) */}
        <div>
          <label style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
            Transaction Type (from Admin perspective)
          </label>
          <div className="ios-segmented-control" style={{ width: '100%', padding: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <button
              type="button"
              onClick={() => setTransactionType('paid')}
              className={`ios-segment-button ${transactionType === 'paid' ? 'active' : ''}`}
              style={{
                padding: '9px 12px',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: transactionType === 'paid' ? 'var(--ios-red)' : 'var(--text-secondary)'
              }}
            >
              Paid by Admin (Debit -)
            </button>

            <button
              type="button"
              onClick={() => setTransactionType('receive')}
              className={`ios-segment-button ${transactionType === 'receive' ? 'active' : ''}`}
              style={{
                padding: '9px 12px',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: transactionType === 'receive' ? 'var(--ios-green)' : 'var(--text-secondary)'
              }}
            >
              Received by Admin (Credit +)
            </button>
          </div>
          <div style={{
            fontSize: '0.74rem',
            color: 'var(--text-tertiary)',
            marginTop: '6px',
            padding: '4px 6px',
            borderRadius: '6px',
            background: 'rgba(118, 118, 128, 0.08)'
          }}>
            {transactionType === 'paid'
              ? '🔴 Paid by Admin: Money given out / paid by Admin for member → Decreases member balance (Member owes Admin)'
              : '🟢 Received by Admin: Money received by Admin from member → Increases member balance (Member clears debt)'}
          </div>
        </div>

        {/* Quick Selection for Recent & Frequent Presets */}
        <QuickExpensePicker
          onSelect={handleQuickPresetSelect}
          selectedDescription={description}
          selectedAmount={amount}
        />

        {/* Amount Input */}
        <div>
          <GlassInput
            label="Amount (₹)"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />

          {/* Quick Amount Chips */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
            {['50', '100', '200', '500', '1000', '2000'].map(val => (
              <button
                key={val}
                type="button"
                onClick={() => setAmount(val)}
                className="glass-button"
                style={{ padding: '4px 10px', fontSize: '0.78rem' }}
              >
                ₹{val}
              </button>
            ))}
          </div>
        </div>

        {/* Category Dropdown */}
        <div>
          <label style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
            Category
          </label>
          <select
            className="glass-select"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Select Category (Optional)</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Description / Item */}
        <GlassInput
          label="Description / Reason"
          placeholder="e.g. Lunch, Tea, GPay transfer, Soda"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        />

        {/* Custom Date & Time */}
        <GlassInput
          label="Transaction Date & Time"
          type="datetime-local"
          value={transactionDate}
          onChange={(e) => setTransactionDate(e.target.value)}
        />

        {/* Receipt Attachment Upload */}
        <ReceiptUploadField
          receiptUrl={receiptUrl}
          onChange={setReceiptUrl}
          onParsed={(data) => {
            if (data.amount && (!amount || amount === '0')) {
              setAmount(data.amount.toString());
            }
            if (data.suggestedCategory) {
              const matched = categories.find(c => c.name.toLowerCase().includes(data.suggestedCategory!.toLowerCase()));
              if (matched) {
                setCategoryId(String(matched.id));
              }
            }
            if (data.suggestedDescription && !description) {
              setDescription(data.suggestedDescription);
            }
          }}
          disabled={isSubmitting}
        />

        {/* Submit & Cancel Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <GlassButton
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            disabled={isSubmitting}
            style={{ flex: 1, padding: '14px' }}
          >
            Cancel
          </GlassButton>
          <GlassButton
            type="submit"
            variant={transactionType === 'receive' ? 'emerald' : 'primary'}
            isLoading={isSubmitting}
            icon={isEditing ? <Edit2 size={18} /> : <Plus size={18} />}
            style={{ flex: 2, padding: '14px' }}
          >
            {isEditing ? 'Save & Recalculate' : 'Commit to Ledger'}
          </GlassButton>
        </div>
      </form>
    </GlassModal>
  );
};
